import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Table,
  Alert,
  Spinner,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Upload, Save, FileText, Check, ChevronRight, Trash2 } from 'lucide-react';
import apiService from '../../../services/api';
import { useOpzioniDropdown, OpzioniOptions } from '../../../components/OpzioniDropdown';
import { 
  Categoria, 
  Clienti, 
  PDFMeasurementsResponse, 
  RefertoCompleto,
  StoricoReferti
} from '../../../types';

const NuovoReferto: React.FC = () => {
  const opzioni = useOpzioniDropdown();
  const navigate = useNavigate();
  const location = useLocation();
  // const { specialtyName } = useParams<{ specialtyName: string }>(); // Non utilizzato al momento
  const isInitialMount = useRef(true);
  
  // Chiave per localStorage
  const STORAGE_KEY = 'nuovo_referto_draft';
  
  // State per dati di base
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // State per ricerca cliente
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Clienti[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<Clienti | null>(null);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // State per ricerca medico referente
  const [searchQueryMedico, setSearchQueryMedico] = useState('');
  const [searchResultsMedico, setSearchResultsMedico] = useState<any[]>([]);
  const [selectedMedico, setSelectedMedico] = useState<any | null>(null);
  const [showSearchResultsMedico, setShowSearchResultsMedico] = useState(false);
  const [showCreateMedico, setShowCreateMedico] = useState(false);
  const [newMedicoData, setNewMedicoData] = useState({ nome: '', cognome: '' });
  
  // State per upload PDF
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfData, setPdfData] = useState<PDFMeasurementsResponse | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  
  // State per form
  const [formData, setFormData] = useState<RefertoCompleto>({
    data_referto: new Date().toISOString().split('T')[0],
    indicazioni_terapeutiche: `Si consiglia integrazione acidi grassi polinsaturi.
Si consigliano esami ematobiochimici ed urine con PU/CU.
Si raccomanda la valutazione della frequenza respiratoria a riposo in un minuto (app cardiodog CEVA), osservando che resti al di sotto dei 30/35 atm/min.
Si raccomanda esercizio fisico controllato, evitare situazioni di stress e caldo.
Si raccomanda visita clinica presso il medico curante per tosse, affaticamento, riluttanza all'esercizio, anoressia, aumento del volume addominale, aumento della frequenza respiratoria, debolezza, sincope, difficoltà respiratoria.`
  });
  
  // State per valori selezionati delle categorie
  const [selectedValues, setSelectedValues] = useState<Record<string, Record<string, number | null>>>({});
  
  // Step wizard
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  
  // Traccia se i dati provengono da una bozza ripristinata
  const [isRestoredDraft, setIsRestoredDraft] = useState(false);

  // Traccia se stiamo modificando una bozza esistente
  const [isEditingBozza, setIsEditingBozza] = useState(false);
  const [editingBozzaId, setEditingBozzaId] = useState<number | null>(null);

  // Funzioni per persistenza dati
  const saveDraftToStorage = useCallback(() => {
    if (isInitialMount.current) return; // Non salvare al primo mount
    
    const draftData = {
      formData,
      selectedValues,
      selectedCliente,
      searchQuery,
      selectedMedico,
      searchQueryMedico,
      newMedicoData,
      pdfData,
      currentStep,
      timestamp: Date.now()
      // Nota: pdfFile non viene salvato nel localStorage perché è un File object
    };
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
      console.log('📝 [DRAFT] Bozza salvata automaticamente');
    } catch (error) {
      console.error('❌ [DRAFT] Errore nel salvataggio bozza:', error);
    }
  }, [formData, selectedValues, selectedCliente, searchQuery, selectedMedico, searchQueryMedico, newMedicoData, pdfData, currentStep]);

  const loadDraftFromStorage = useCallback(() => {
    try {
      const savedDraft = localStorage.getItem(STORAGE_KEY);
      if (savedDraft) {
        const draftData = JSON.parse(savedDraft);
        const timeDiff = Date.now() - draftData.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 ore
        
        if (timeDiff < maxAge) {
          console.log('📂 [DRAFT] Caricamento bozza salvata...');
          
          // Ripristina i dati
          if (draftData.formData) setFormData(draftData.formData);
          if (draftData.selectedValues) setSelectedValues(draftData.selectedValues);
          if (draftData.selectedCliente) {
            setSelectedCliente(draftData.selectedCliente);
            setSearchQuery(`${draftData.selectedCliente.nome} ${draftData.selectedCliente.cognome}`);
          }
          if (draftData.searchQuery && !draftData.selectedCliente) setSearchQuery(draftData.searchQuery);
          if (draftData.selectedMedico) {
            setSelectedMedico(draftData.selectedMedico);
            setSearchQueryMedico(`${draftData.selectedMedico.nome} ${draftData.selectedMedico.cognome || ''}`);
          }
          if (draftData.searchQueryMedico && !draftData.selectedMedico) setSearchQueryMedico(draftData.searchQueryMedico);
          if (draftData.newMedicoData) setNewMedicoData(draftData.newMedicoData);
          if (draftData.pdfData) setPdfData(draftData.pdfData);
          if (draftData.currentStep) setCurrentStep(draftData.currentStep);
          
          toast.info('Bozza precedente ripristinata automaticamente', { 
            autoClose: 3000,
            hideProgressBar: false 
          });
          
          return true; // Indica che i dati sono stati caricati
        } else {
          // Bozza troppo vecchia, elimina
          localStorage.removeItem(STORAGE_KEY);
          console.log('🗑️ [DRAFT] Bozza scaduta eliminata');
        }
      }
    } catch (error) {
      console.error('❌ [DRAFT] Errore nel caricamento bozza:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
    return false;
  }, []);

  const clearDraftFromStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ [DRAFT] Bozza eliminata dal localStorage');
  }, []);

  const loadDataFromStoricoReferto = useCallback(async (storicoReferto: StoricoReferti, categorieData: Categoria[]) => {
    console.log('🔄 [DUPLICATE] Inizio conversione dati storico referto:', storicoReferto);
    
    const referto = storicoReferto.referto;
    const cliente = storicoReferto.clienti;
    
    // Precompila il form con i dati del referto
    const loadedFormData: any = {
      data_referto: referto.data || new Date().toISOString().split('T')[0],
      indicazioni_terapeutiche: referto.indicazioni || `Si consiglia integrazione acidi grassi polinsaturi.
Si consigliano esami ematobiochimici ed urine con PU/CU.
Si raccomanda la valutazione della frequenza respiratoria a riposo in un minuto (app cardiodog CEVA), osservando che resti al di sotto dei 30/35 atm/min.
Si raccomanda esercizio fisico controllato, evitare situazioni di stress e caldo.
Si raccomanda visita clinica presso il medico curante per tosse, affaticamento, riluttanza all'esercizio, anoressia, aumento del volume addominale, aumento della frequenza respiratoria, debolezza, sincope, difficoltà respiratoria.`
    };

    // Dati paziente
    if (referto.paziente) {
      if (referto.paziente.nome) loadedFormData.nome_paziente = referto.paziente.nome;
      if (referto.paziente.razza) loadedFormData.razza_paziente = referto.paziente.razza;
      if (referto.paziente.sesso) loadedFormData.sesso_paziente = referto.paziente.sesso;
      if (referto.paziente.specie) loadedFormData.specie_paziente = referto.paziente.specie;
      if (referto.paziente.peso) loadedFormData.peso_paziente = referto.paziente.peso;
      if (referto.paziente.age) loadedFormData.age_paziente = referto.paziente.age;
    }

    // Altri campi del referto
    const fieldMapping = [
      'motivo_visita', 'anamnesi', 'note', 'note_eco',
      'frequenza_cardiaca', 'ritmo_cardiaco', 'mucose', 'trc',
      'itto_cardiaco', 'polso_femorale', 'polso_giugulare', 'attivita', 'tosse'
    ];

    fieldMapping.forEach(field => {
      const value = (referto as any)[field];
      if (value && value.toString().trim() !== '') {
        if (field === 'motivo_visita') {
          loadedFormData.motivo_visita = value;
        } else if (field === 'note') {
          loadedFormData.note_esame = value;
        } else {
          loadedFormData[field] = value;
        }
      }
    });

    // Campi specifici
    if (referto.terapia_in_atto) loadedFormData.terapia_attuale = referto.terapia_in_atto;
    if (referto.conclusioni) loadedFormData.conclusioni_referto = referto.conclusioni;
    if (referto.controllo) loadedFormData.prossimo_controllo = referto.controllo;
    // Gestione medico referente
    if (referto.medico_referente) {
      if (referto.medico_referente.id) {
        // Se ha un ID, impostalo come medico esistente
        loadedFormData.medico_referente = referto.medico_referente.id;
        setSelectedMedico(referto.medico_referente);
        setSearchQueryMedico(`${referto.medico_referente.nome} ${referto.medico_referente.cognome || ''}`.trim());
      } else if (referto.medico_referente.nome) {
        // Se non ha ID ma ha nome, crea nuovo medico
        loadedFormData.nome_medico_referente = referto.medico_referente.nome;
        loadedFormData.cognome_medico_referente = referto.medico_referente.cognome || '';
        setSearchQueryMedico(`${referto.medico_referente.nome} ${referto.medico_referente.cognome || ''}`.trim());
      }
      // Mantieni anche il campo legacy per compatibilità
      loadedFormData.medico_ref = `${referto.medico_referente.nome} ${referto.medico_referente.cognome || ''}`.trim();
    }

    // Dati cliente
    if (cliente) {
      loadedFormData.cliente_id = cliente.id;
      loadedFormData.cliente_nome = cliente.nome;
      loadedFormData.cliente_cognome = cliente.cognome;
      loadedFormData.cliente_indirizzo = cliente.indirizzo;
      loadedFormData.cliente_citta = cliente.citta;
      loadedFormData.cliente_codice_fiscale = cliente.codice_fiscale;
      loadedFormData.cliente_email = cliente.email;

      setSelectedCliente(cliente);
      setSearchQuery(`${cliente.nome} ${cliente.cognome}`.trim());
    }

    // Se ci sono misurazioni PDF nel referto
    if (referto.misurazioni_pdf && Object.keys(referto.misurazioni_pdf).length > 0) {
      console.log('📊 [DUPLICATE] Caricamento misurazioni PDF:', referto.misurazioni_pdf);
      setPdfData({
        measurements: referto.misurazioni_pdf,
        patient_name: referto.paziente?.nome || '',
        exam_date: referto.data || ''
      });
    }

    setFormData(loadedFormData);
    console.log('✅ [DUPLICATE] FormData caricato:', loadedFormData);

    // TODO: Gestire i valori delle categorie se presenti
    // Per ora inizializziamo valori vuoti
    initializeSelectedValues(categorieData);

    setIsRestoredDraft(true);
    setIsEditingBozza(false); // Non è una modifica bozza ma un referto duplicato
    setEditingBozzaId(null);
    clearDraftFromStorage();

    toast.success('Dati del referto duplicato caricati con successo', { 
      autoClose: 3000,
      hideProgressBar: false 
    });
  }, [clearDraftFromStorage]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [categorieRes] = await Promise.all([
        apiService.getCategories()
      ]);
      
      setCategorie(categorieRes || []);
      
      // Controlla se arriva da navigazione per modificare una bozza
      const isEditingBozza = location.state?.editBozza;
      const bozzaId = location.state?.bozzaId;
      const shouldClearDraft = location.state?.clearDraft;
      const isDuplicated = location.state?.isDuplicated; // Flag per referto duplicato
      
      console.log('🔍 [NAVIGATION] Parametri ricevuti dalla navigazione:', {
        isEditingBozza,
        bozzaId,
        shouldClearDraft,
        isDuplicated,
        fullLocationState: location.state
      });
      
      if (isEditingBozza && bozzaId) {
        if (isDuplicated) {
          // Referto duplicato: carica i dati dello storico referto
          console.log('🔄 [DUPLICATE] Caricamento dati referto duplicato, ID:', bozzaId);
          try {
            console.log('📡 [DUPLICATE] Chiamata API getReferto con ID:', bozzaId);
            console.log('📡 [DUPLICATE] URL che verrà chiamato: /api/referti/' + bozzaId + '/');
            
            const storicoReferto = await apiService.getReferto(bozzaId);
            console.log('✅ [DUPLICATE] Risposta API storico referto:', storicoReferto);
            
            // Converti i dati dello storico referto nel formato per la modifica
            await loadDataFromStoricoReferto(storicoReferto, categorieRes || []);
          } catch (error) {
            console.error('❌ [DUPLICATE] Errore nel caricamento del referto duplicato:', error);
            toast.error('Errore nel caricamento dei dati del referto duplicato');
            // Fallback ai dati vuoti
            initializeSelectedValues(categorieRes || []);
            setIsRestoredDraft(false);
          }
        } else {
          // Modifica bozza normale: carica i dati della bozza dall'API
          console.log('✏️ [BOZZA] Caricamento dati bozza per modifica, ID:', bozzaId);
          try {
            console.log('📡 [BOZZA] Chiamata API getBozza con ID:', bozzaId);
            console.log('📡 [BOZZA] URL che verrà chiamato: /api/bozze/' + bozzaId + '/');
            
            const response = await apiService.getBozza(bozzaId);
            console.log('✅ [BOZZA] Risposta API completa:', response);
            console.log('✅ [BOZZA] Tipo risposta:', typeof response);
            console.log('✅ [BOZZA] Chiavi risposta:', Object.keys(response || {}));
            
            // I dati effettivi sono nel campo 'data' della risposta
            const bozzaData = response.data;
            console.log('✅ [BOZZA] Dati bozza estratti:', bozzaData);
            
            // Precompila il form con i dati della bozza
            if (bozzaData) {
              // Inizializza i valori delle categorie
              initializeSelectedValues(categorieRes || []);
              
              // Precompila i dati del form con quelli presenti nella bozza
              const loadedFormData: any = {
                data_referto: bozzaData.data_referto || new Date().toISOString().split('T')[0],
                indicazioni_terapeutiche: bozzaData.indicazioni_terapeutiche || `Si consiglia integrazione acidi grassi polinsaturi.
Si consigliano esami ematobiochimici ed urine con PU/CU.
Si raccomanda la valutazione della frequenza respiratoria a riposo in un minuto (app cardiodog CEVA), osservando che resti al di sotto dei 30/35 atm/min.
Si raccomanda esercizio fisico controllato, evitare situazioni di stress e caldo.
Si raccomanda visita clinica presso il medico curante per tosse, affaticamento, riluttanza all'esercizio, anoressia, aumento del volume addominale, aumento della frequenza respiratoria, debolezza, sincope, difficoltà respiratoria.`
              };
            
            // Aggiungi tutti i campi della bozza al form solo se hanno un valore
            const fieldMappings = [
              'nome_paziente', 'razza_paziente', 'sesso_paziente', 'specie_paziente', 'peso_paziente', 'age_paziente',
              'motivo_visita', 'terapia_attuale', 'anamnesi', 'conclusioni_referto', 'prossimo_controllo',
              'note_esame', 'note_eco', 'medico_ref', 'frequenza_cardiaca', 'ritmo_cardiaco',
              'mucose', 'trc', 'itto_cardiaco', 'polso_femorale', 'polso_giugulare', 'attivita', 'tosse'
            ];
            
            fieldMappings.forEach(field => {
              if (bozzaData[field] && bozzaData[field].toString().trim() !== '') {
                loadedFormData[field] = bozzaData[field];
                console.log(`📝 [BOZZA] Campo ${field} caricato:`, bozzaData[field]);
              }
            });
            
            // Carica i dati del cliente se presenti
            if (bozzaData.cliente_id) {
              console.log('👤 [BOZZA] Caricamento dati cliente:', {
                id: bozzaData.cliente_id,
                nome: bozzaData.cliente_nome,
                cognome: bozzaData.cliente_cognome
              });
              
              // Aggiungi i dati del cliente al form solo se presenti
              if (bozzaData.cliente_nome && bozzaData.cliente_nome.trim() !== '') {
                loadedFormData.cliente_nome = bozzaData.cliente_nome;
              }
              if (bozzaData.cliente_cognome && bozzaData.cliente_cognome.trim() !== '') {
                loadedFormData.cliente_cognome = bozzaData.cliente_cognome;
              }
              if (bozzaData.cliente_indirizzo && bozzaData.cliente_indirizzo.trim() !== '') {
                loadedFormData.cliente_indirizzo = bozzaData.cliente_indirizzo;
              }
              if (bozzaData.cliente_citta && bozzaData.cliente_citta.trim() !== '') {
                loadedFormData.cliente_citta = bozzaData.cliente_citta;
              }
              if (bozzaData.cliente_codice_fiscale && bozzaData.cliente_codice_fiscale.trim() !== '') {
                loadedFormData.cliente_codice_fiscale = bozzaData.cliente_codice_fiscale;
              }
              if (bozzaData.cliente_email && bozzaData.cliente_email.trim() !== '') {
                loadedFormData.cliente_email = bozzaData.cliente_email;
              }
              
              loadedFormData.cliente_id = bozzaData.cliente_id;
              
              // Imposta il cliente selezionato se abbiamo almeno nome e cognome
              if (bozzaData.cliente_nome && bozzaData.cliente_cognome) {
                setSelectedCliente({
                  id: bozzaData.cliente_id,
                  nome: bozzaData.cliente_nome,
                  cognome: bozzaData.cliente_cognome,
                  indirizzo: bozzaData.cliente_indirizzo || '',
                  citta: bozzaData.cliente_citta || '',
                  codice_fiscale: bozzaData.cliente_codice_fiscale || '',
                  email: bozzaData.cliente_email || ''
                } as any);
                setSearchQuery(`${bozzaData.cliente_nome} ${bozzaData.cliente_cognome}`.trim());
              }
            }
            
            setFormData(loadedFormData);
            console.log('📝 [BOZZA] FormData finale caricato:', loadedFormData);
            
            // Se ci sono misurazioni PDF, caricale
            if (bozzaData.pdf_measurements && Object.keys(bozzaData.pdf_measurements).length > 0) {
              console.log('📊 [BOZZA] Caricamento misurazioni PDF:', bozzaData.pdf_measurements);
              try {
                const measurements = typeof bozzaData.pdf_measurements === 'string' 
                  ? JSON.parse(bozzaData.pdf_measurements)
                  : bozzaData.pdf_measurements;
                
                setPdfData({
                  measurements,
                  patient_name: bozzaData.nome_paziente || '',
                  exam_date: bozzaData.data_referto || ''
                });
                console.log('✅ [BOZZA] Misurazioni PDF caricate con successo');
              } catch (e) {
                console.warn('⚠️ [BOZZA] Errore nel parsing delle misurazioni PDF:', e);
              }
            } else {
              console.log('ℹ️ [BOZZA] Nessuna misurazione PDF presente');
            }
            
            // Se ci sono valori delle categorie, caricali
            if (bozzaData.valori && bozzaData.valori.length > 0) {
              console.log('📝 [BOZZA] Valori categorie da caricare:', bozzaData.valori);
              try {
                // I valori potrebbero essere un array o una stringa JSON
                let valoriIds: number[] = [];
                if (Array.isArray(bozzaData.valori)) {
                  valoriIds = bozzaData.valori;
                } else if (typeof bozzaData.valori === 'string') {
                  valoriIds = JSON.parse(bozzaData.valori);
                }
                
                if (valoriIds.length > 0) {
                  loadSelectedValuesFromBozza(categorieRes || [], valoriIds);
                } else {
                  // Inizializza valori vuoti
                  initializeSelectedValues(categorieRes || []);
                }
              } catch (e) {
                console.warn('⚠️ [BOZZA] Errore nel parsing dei valori categorie:', e);
                // Fallback: inizializza valori vuoti
                initializeSelectedValues(categorieRes || []);
              }
            } else {
              console.log('ℹ️ [BOZZA] Nessun valore di categoria presente');
              // Inizializza valori vuoti
              initializeSelectedValues(categorieRes || []);
            }
            
            setIsRestoredDraft(true);
            setIsEditingBozza(true);
            setEditingBozzaId(bozzaId);
            clearDraftFromStorage(); // Pulisci il localStorage per evitare conflitti
              
              toast.success('Dati della bozza caricati con successo', { 
                autoClose: 3000,
                hideProgressBar: false 
              });
            }
          } catch (error) {
            console.error('❌ [BOZZA] Errore nel caricamento della bozza:', error);
            toast.error('Errore nel caricamento dei dati della bozza');
            // Fallback ai dati vuoti
            initializeSelectedValues(categorieRes || []);
            setIsRestoredDraft(false);
          }
        }
      } else if (shouldClearDraft) {
        // Navigazione intenzionale: pulisce la bozza e parte con form pulito
        clearDraftFromStorage();
        initializeSelectedValues(categorieRes || []);
        setIsRestoredDraft(false);
        setPdfFile(null); // Pulisci anche il file PDF
      } else {
        // Refresh della pagina: prova a caricare la bozza
        const draftLoaded = loadDraftFromStorage();
        
        if (draftLoaded) {
          // Bozza caricata con successo
          setIsRestoredDraft(true);
        } else {
          // Nessuna bozza: inizializza valori vuoti
          initializeSelectedValues(categorieRes || []);
          setIsRestoredDraft(false);
        }
      }
      
      // Dopo il primo caricamento, abilita il salvataggio automatico
      setTimeout(() => {
        isInitialMount.current = false;
      }, 1000);
      
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
      toast.error('Errore nel caricamento dei dati');
      setCategorie([]);
    } finally {
      setLoading(false);
    }
  }, [loadDraftFromStorage, clearDraftFromStorage, loadDataFromStoricoReferto, location.state]);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Rimossa dipendenza loadInitialData per evitare chiamate duplicate

  // Salvataggio automatico quando cambiano i dati
  useEffect(() => {
    saveDraftToStorage();
  }, [saveDraftToStorage]);

  // DEBUG: Log degli stati di ricerca
  useEffect(() => {
    console.log('RENDER DEBUG - showSearchResults:', showSearchResults, 'searchResults.length:', searchResults.length);
  }, [showSearchResults, searchResults]);

  // Pulizia al unmount del componente
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Se l'utente sta navigando via dalla pagina (chiude tab, va su altro sito)
      // la bozza viene mantenuta per permettere il ripristino
    };

    const handlePopState = () => {
      // Se l'utente usa il back button del browser
      clearDraftFromStorage();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      
      // Se si sta navigando verso la dashboard o altre pagine dell'app
      const currentPath = location.pathname;
      if (!currentPath.includes('/referti/nuovo')) {
        clearDraftFromStorage();
      }
    };
  }, [clearDraftFromStorage, location.pathname]);

  const initializeSelectedValues = (categorieData: Categoria[]) => {
    const initialValues: Record<string, Record<string, number | null>> = {};
    categorieData.forEach((categoria: any) => {
      initialValues[categoria.categoria] = {};
      categoria.campi.forEach((campo: any) => {
        initialValues[categoria.categoria][campo.campo] = null;
      });
    });
    setSelectedValues(initialValues);
  };


  const loadSelectedValuesFromBozza = (categorieData: Categoria[], valoriIds: number[]) => {
    // Crea una mappa inversa da ID valore a categoria/campo
    const valueToFieldMap: Record<number, { categoria: string; campo: string }> = {};
    
    categorieData.forEach((categoria: any) => {
      categoria.campi.forEach((campo: any) => {
        campo.valori.forEach((valore: any) => {
          valueToFieldMap[valore.id] = {
            categoria: categoria.categoria,
            campo: campo.campo
          };
        });
      });
    });

    // Inizializza selectedValues vuoto
    const newSelectedValues: Record<string, Record<string, number | null>> = {};
    categorieData.forEach((categoria: any) => {
      newSelectedValues[categoria.categoria] = {};
      categoria.campi.forEach((campo: any) => {
        newSelectedValues[categoria.categoria][campo.campo] = null;
      });
    });

    // Popola con i valori salvati
    valoriIds.forEach(valoreId => {
      const fieldMapping = valueToFieldMap[valoreId];
      if (fieldMapping) {
        newSelectedValues[fieldMapping.categoria][fieldMapping.campo] = valoreId;
        console.log(`📝 [BOZZA] Caricato valore categoria:`, {
          categoria: fieldMapping.categoria,
          campo: fieldMapping.campo,
          valoreId: valoreId
        });
      } else {
        console.warn(`⚠️ [BOZZA] Valore ID ${valoreId} non trovato nelle categorie disponibili`);
      }
    });

    setSelectedValues(newSelectedValues);
    console.log('✅ [BOZZA] Valori categorie caricati:', newSelectedValues);
  };

  // Gestione ricerca clienti
  const handleClientSearch = async (query: string) => {
    setSearchQuery(query);
    
    // Se c'è un cliente selezionato e il testo non corrisponde più, resettalo
    if (selectedCliente && query !== `${selectedCliente.nome} ${selectedCliente.cognome}`) {
      setSelectedCliente(null);
      setFormData((prev: any) => ({
        ...prev,
        cliente_id: undefined,
        cliente_nome: undefined,
        cliente_cognome: undefined,
        cliente_indirizzo: undefined,
        cliente_citta: undefined,
        cliente_codice_fiscale: undefined,
        cliente_email: undefined
      }));
    }
    
    if (query.length > 2) {
      try {
        const response = await apiService.searchCliente(query);
        console.log('Risposta ricerca clienti:', response);
        
        // Gestisci la struttura paginata della risposta API
        let clientiResults: Clienti[] = [];
        if (response && typeof response === 'object' && 'results' in response) {
          // Struttura paginata: estrai i clienti da response.results[].cliente
          clientiResults = response.results?.map((item: any) => item.cliente) || [];
        } else if (Array.isArray(response)) {
          // Array semplice di clienti
          clientiResults = response;
        }
        
        setSearchResults(clientiResults);
        setShowSearchResults(true);
        console.log('Clienti estratti per il dropdown:', clientiResults);
        console.log('showSearchResults impostato a:', true);
        console.log('searchResults.length:', clientiResults.length);
      } catch (error) {
        console.error('Errore nella ricerca clienti:', error);
        setSearchResults([]);
        setShowSearchResults(false);
        // Non mostrare toast error per non disturbare l'utente durante la digitazione
      }
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const selectCliente = (cliente: Clienti) => {
    setSelectedCliente(cliente);
    setSearchQuery(`${cliente.nome} ${cliente.cognome}`);
    setShowSearchResults(false);
    setFormData((prev: any) => ({
      ...prev,
      cliente_id: cliente.id,
      cliente_nome: cliente.nome || undefined,
      cliente_cognome: cliente.cognome || undefined,
      cliente_indirizzo: cliente.indirizzo || undefined,
      cliente_citta: cliente.citta || undefined,
      cliente_codice_fiscale: cliente.codice_fiscale || undefined,
      cliente_email: cliente.email || undefined
    }));
  };

  // Gestione ricerca medico referente
  const handleMedicoSearch = async (query: string) => {
    setSearchQueryMedico(query);
    
    // Se c'è un medico selezionato e il testo non corrisponde più, resettalo
    if (selectedMedico && query !== `${selectedMedico.nome} ${selectedMedico.cognome || ''}`) {
      setSelectedMedico(null);
      setFormData((prev: any) => ({
        ...prev,
        medico_referente: undefined,
        nome_medico_referente: undefined,
        cognome_medico_referente: undefined
      }));
    }
    
    if (query.length > 2) {
      try {
        const response = await apiService.searchMedico(query);
        console.log('Risposta ricerca medici:', response);
        
        // Gestisci la struttura della risposta API (simile ai clienti)
        let mediciResults: any[] = [];
        if (response && typeof response === 'object' && 'results' in response) {
          mediciResults = response.results?.map((item: any) => item.medico || item) || [];
        } else if (Array.isArray(response)) {
          mediciResults = response;
        }
        
        setSearchResultsMedico(mediciResults);
        setShowSearchResultsMedico(true);
        setShowCreateMedico(mediciResults.length === 0);
        console.log('Medici estratti per il dropdown:', mediciResults);
      } catch (error) {
        console.error('Errore nella ricerca medici:', error);
        setSearchResultsMedico([]);
        setShowSearchResultsMedico(false);
        setShowCreateMedico(true);
      }
    } else {
      setSearchResultsMedico([]);
      setShowSearchResultsMedico(false);
      setShowCreateMedico(false);
    }
  };

  const selectMedico = (medico: any) => {
    setSelectedMedico(medico);
    setSearchQueryMedico(`${medico.nome} ${medico.cognome || ''}`);
    setShowSearchResultsMedico(false);
    setShowCreateMedico(false);
    setFormData((prev: any) => ({
      ...prev,
      medico_referente: medico.id,
      nome_medico_referente: undefined,
      cognome_medico_referente: undefined
    }));
  };

  const handleCreateMedico = () => {
    if (newMedicoData.nome.trim() && newMedicoData.cognome.trim()) {
      setFormData((prev: any) => ({
        ...prev,
        medico_referente: undefined,
        nome_medico_referente: newMedicoData.nome.trim(),
        cognome_medico_referente: newMedicoData.cognome.trim()
      }));
      setSearchQueryMedico(`${newMedicoData.nome.trim()} ${newMedicoData.cognome.trim()}`);
      setShowCreateMedico(false);
      setNewMedicoData({ nome: '', cognome: '' });
    }
  };

  // Gestione upload PDF
  const handlePDFUpload = async (file: File) => {
    if (!file) return;
    
    setPdfLoading(true);
    try {
      const result = await apiService.uploadPDFMeasurements(file);
      setPdfData(result);
      setPdfFile(file); // Salva il file originale per il salvataggio bozza
      toast.success('PDF elaborato con successo');
    } catch (error) {
      console.error('Errore upload PDF:', error);
      toast.error('Errore durante l\'elaborazione del PDF');
      setPdfData(null);
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
    }
  };

  // Gestione valori categorie
  const updateSelectedValue = (categoria: string, campo: string, valueId: number | null) => {
    setSelectedValues(prev => ({
      ...prev,
      [categoria]: {
        ...prev[categoria],
        [campo]: valueId
      }
    }));
  };

  // Gestione form
  const handleInputChange = (field: keyof RefertoCompleto, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // Navigazione step
  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const goToStep = (step: number) => {
    if (step <= currentStep || validateStepsUpTo(step - 1)) {
      setCurrentStep(step);
    }
  };

  // Validazioni per step
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return validateStep1(); // Cliente
      case 2:
        return validateStep3(); // Dati Generali
      case 3:
        return true; // Ecocardiografia opzionale
      case 4:
        return validateStep2(); // Anamnesi
      case 5:
        return true; // Esame clinico opzionale
      case 6:
        return validateStep6(); // Conclusioni
      default:
        return true;
    }
  };

  const validateStep1 = (): boolean => {
    if (!selectedCliente && !formData.cliente_nome) {
      toast.error('Seleziona un cliente o inserisci i dati del cliente');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    return true; // Anamnesi opzionale
  };

  const validateStep3 = (): boolean => {
    if (!formData.data_referto) {
      toast.error('Inserisci la data del referto');
      return false;
    }
    return true;
  };

  const validateStep6 = (): boolean => {
    return true; // Conclusioni opzionali
  };

  const validateStepsUpTo = (step: number): boolean => {
    for (let i = 1; i <= step; i++) {
      const currentStepTemp = currentStep;
      setCurrentStep(i);
      const isValid = validateCurrentStep();
      setCurrentStep(currentStepTemp);
      if (!isValid) return false;
    }
    return true;
  };

  // Etichette degli step
  const getStepLabel = (step: number): string => {
    switch (step) {
      case 1: return 'Cliente';
      case 2: return 'Dati Generali';
      case 3: return 'Esame Obiettivo';
      case 4: return 'Anamnesi';
      case 5: return 'Esame Clinico';
      case 6: return 'Conclusioni';
      default: return `Step ${step}`;
    }
  };

  const getStepDescription = (step: number): string => {
    switch (step) {
      case 1: return 'Seleziona il cliente';
      case 2: return 'Informazioni generali del referto';
      case 3: return 'Descrizione ecocardiografica';
      case 4: return 'Anamnesi del paziente';
      case 5: return 'Parametri dell\'esame clinico';
      case 6: return 'Conclusioni e azioni finali';
      default: return '';
    }
  };

  // Salva bozza
  const handleSaveBozza = async () => {
    try {
      if (isEditingBozza && editingBozzaId) {
        // Aggiorna bozza esistente
        const bozzaData = prepareBozzaDataWithFile();
        await apiService.updateBozza(editingBozzaId, bozzaData);
        toast.success('Bozza aggiornata con successo');
      } else {
        // Crea nuova bozza
        const bozzaData = prepareBozzaDataWithFile();
        const newBozza = await apiService.saveBozza(bozzaData);
        toast.success('Bozza salvata con successo');
        
        // Aggiorna lo stato per permettere aggiornamenti successivi
        setIsEditingBozza(true);
        setEditingBozzaId(newBozza.id);
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio della bozza');
    }
  };

  // Genera referto
  const handleGenerateReferto = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pdfData) {
      toast.error('Caricare prima il PDF con le misurazioni');
      return;
    }

    if (!selectedCliente && !formData.cliente_nome) {
      toast.error('Selezionare un cliente o inserire i dati del cliente');
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = prepareFormData();
      const result = await apiService.generateReferto(formDataToSend);
      
      if (result.status === 'success') {
        // Pulisci la bozza dal localStorage dopo il successo
        clearDraftFromStorage();
        toast.success('Referto generato con successo');
        navigate(result.data.redirect_url || '/referti');
      } else {
        toast.error(result.message || 'Errore durante la generazione del referto');
      }
    } catch (error) {
      toast.error('Errore durante la generazione del referto');
    } finally {
      setSubmitting(false);
    }
  };



  const prepareBozzaDataWithFile = () => {
    const formDataToSend = new FormData();
    
    // Valori selezionati delle categorie
    const selectedIds: number[] = [];
    Object.values(selectedValues).forEach(category => {
      Object.values(category).forEach(valueId => {
        if (valueId) selectedIds.push(valueId);
      });
    });

    // Dati cliente
    if (selectedCliente) {
      formDataToSend.append('cliente_id', selectedCliente.id.toString());
    } else {
      if (formData.cliente_nome) formDataToSend.append('cliente_nome', formData.cliente_nome);
      if (formData.cliente_cognome) formDataToSend.append('cliente_cognome', formData.cliente_cognome);
      if (formData.cliente_indirizzo) formDataToSend.append('cliente_indirizzo', formData.cliente_indirizzo);
      if (formData.cliente_citta) formDataToSend.append('cliente_citta', formData.cliente_citta);
      if (formData.cliente_codice_fiscale) formDataToSend.append('cliente_codice_fiscale', formData.cliente_codice_fiscale);
      if (formData.cliente_email) formDataToSend.append('cliente_email', formData.cliente_email);
    }

    // Aggiungi il file PDF se presente
    if (pdfFile) {
      formDataToSend.append('pdf_file', pdfFile);
      console.log('📤 [BOZZA] File PDF aggiunto:', pdfFile.name);
    }

    // Dati medico referente per bozze
    if (formData.medico_referente) {
      formDataToSend.append('medico_referente', formData.medico_referente.toString());
    } else if (formData.nome_medico_referente && formData.cognome_medico_referente) {
      formDataToSend.append('nome_medico_referente', formData.nome_medico_referente);
      formDataToSend.append('cognome_medico_referente', formData.cognome_medico_referente);
    }

    // Aggiungi i measurements del PDF se presenti
    if (pdfData && pdfData.measurements) {
      formDataToSend.append('pdf_measurements', JSON.stringify(pdfData.measurements));
    }

    // Aggiungi valori selezionati
    formDataToSend.append('valori', JSON.stringify(selectedIds));

    // Aggiungi specialistica dal localStorage
    const specialistica = localStorage.getItem('selectedSpecialty') || '';
    formDataToSend.append('specialistica', specialistica);

    // Mapping esplicito dei campi per l'API Django
    const fieldMapping = {
      'data_referto': 'data_referto',
      'motivo_visita': 'motivo_visita', 
      'terapia_attuale': 'terapia_attuale',
      'anamnesi': 'anamnesi',
      'conclusioni_referto': 'conclusioni_referto',
      'indicazioni_terapeutiche': 'indicazioni_terapeutiche',
      'prossimo_controllo': 'prossimo_controllo',
      'note_esame': 'note_esame',
      'note_eco': 'note_eco',
      'nome_paziente': 'nome_paziente',
      'razza_paziente': 'razza_paziente',
      'sesso_paziente': 'sesso_paziente',
      'specie_paziente': 'specie_paziente',
      'peso_paziente': 'peso_paziente',
      'age_paziente': 'age_paziente',
      'medico_ref': 'medico_ref',
      'frequenza_cardiaca': 'frequenza_cardiaca',
      'ritmo_cardiaco': 'ritmo_cardiaco',
      'mucose': 'mucose',
      'trc': 'trc',
      'itto_cardiaco': 'itto_cardiaco',
      'polso_femorale': 'polso_femorale',
      'polso_giugulare': 'polso_giugulare',
      'attivita': 'attivita',
      'tosse': 'tosse'
    };
    
    // Aggiungi tutti i campi mappati
    Object.entries(fieldMapping).forEach(([frontendKey, backendKey]) => {
      const value = formData[frontendKey as keyof RefertoCompleto];
      if (value && value.toString().trim()) {
        formDataToSend.append(backendKey, value.toString());
      }
    });

    console.log('📤 [BOZZA] FormData preparato con', Array.from(formDataToSend.keys()).length, 'campi');
    console.log('📤 [BOZZA] Campi:', Array.from(formDataToSend.keys()));
    
    return formDataToSend;
  };

  const prepareFormData = () => {
    const formDataToSend = new FormData();
    
    // Debug: Log dei dati che stiamo per inviare
    console.log('🚀 [FORM] Preparazione dati per API:', {
      selectedCliente,
      formData,
      pdfData: !!pdfData,
      selectedValues: Object.keys(selectedValues).length
    });
    
    // Dati cliente
    if (selectedCliente) {
      formDataToSend.append('cliente_id', selectedCliente.id.toString());
      console.log('📤 [FORM] Cliente ID:', selectedCliente.id);
    } else {
      // Usa i nomi di campo che l'API Django si aspetta
      if (formData.cliente_nome) formDataToSend.append('cliente_nome', formData.cliente_nome);
      if (formData.cliente_cognome) formDataToSend.append('cliente_cognome', formData.cliente_cognome);
      if (formData.cliente_indirizzo) formDataToSend.append('cliente_indirizzo', formData.cliente_indirizzo);
      if (formData.cliente_citta) formDataToSend.append('cliente_citta', formData.cliente_citta);
      if (formData.cliente_codice_fiscale) formDataToSend.append('cliente_codice_fiscale', formData.cliente_codice_fiscale);
      if (formData.cliente_email) formDataToSend.append('cliente_email', formData.cliente_email);
      console.log('📤 [FORM] Nuovo cliente:', {
        nome: formData.cliente_nome,
        cognome: formData.cliente_cognome
      });
    }
    
    // Dati paziente (obbligatori e opzionali)
    if (formData.nome_paziente) {
      formDataToSend.append('nome_paziente', formData.nome_paziente);
      console.log('📤 [FORM] Nome paziente:', formData.nome_paziente);
    } else {
      console.error('❌ [FORM] ERRORE: nome_paziente mancante!');
    }
    
    // Altri dati del paziente
    if (formData.razza_paziente) formDataToSend.append('razza_paziente', formData.razza_paziente);
    if (formData.sesso_paziente) formDataToSend.append('sesso_paziente', formData.sesso_paziente);
    if (formData.specie_paziente) formDataToSend.append('specie_paziente', formData.specie_paziente);
    if (formData.peso_paziente) formDataToSend.append('peso_paziente', formData.peso_paziente);
    if (formData.age_paziente) formDataToSend.append('age_paziente', formData.age_paziente);
    // Dati medico referente
    if (formData.medico_referente) {
      formDataToSend.append('medico_referente', formData.medico_referente.toString());
      console.log('📤 [FORM] Medico referente ID:', formData.medico_referente);
    } else if (formData.nome_medico_referente && formData.cognome_medico_referente) {
      formDataToSend.append('nome_medico_referente', formData.nome_medico_referente);
      formDataToSend.append('cognome_medico_referente', formData.cognome_medico_referente);
      console.log('📤 [FORM] Nuovo medico referente:', {
        nome: formData.nome_medico_referente,
        cognome: formData.cognome_medico_referente
      });
    }
    
    // Mantieni il campo legacy per compatibilità
    if (formData.medico_ref) formDataToSend.append('medico_ref', formData.medico_ref);
    
    // Dati PDF
    if (pdfData && pdfData.measurements) {
      formDataToSend.append('pdf_measurements', JSON.stringify(pdfData.measurements));
      console.log('📤 [FORM] PDF measurements:', Object.keys(pdfData.measurements).length, 'sezioni');
    } else {
      console.warn('⚠️ [FORM] PDF measurements mancanti');
    }
    
    // Valori selezionati delle categorie
    const selectedIds: number[] = [];
    Object.values(selectedValues).forEach(category => {
      Object.values(category).forEach(valueId => {
        if (valueId) selectedIds.push(valueId);
      });
    });
    formDataToSend.append('valori', JSON.stringify(selectedIds));
    console.log('📤 [FORM] Valori selezionati:', selectedIds.length);
    
    // Aggiungi specialistica per cardiologia
    formDataToSend.append('specialistica', '1');
    
    // Mapping esplicito dei campi per l'API Django
    const fieldMapping = {
      'data_referto': 'data_referto',
      'motivo_visita': 'motivo_visita', 
      'terapia_attuale': 'terapia_in_atto',
      'anamnesi': 'anamnesi',
      'conclusioni_referto': 'conclusioni',
      'indicazioni_terapeutiche': 'indicazioni_terapeutiche',
      'prossimo_controllo': 'controllo',
      'note_esame': 'note_esame',
      'note_eco': 'note_eco',
      'frequenza_cardiaca': 'frequenza_cardiaca',
      'ritmo_cardiaco': 'ritmo_cardiaco',
      'mucose': 'mucose',
      'trc': 'trc',
      'itto_cardiaco': 'itto_cardiaco',
      'polso_femorale': 'polso_femorale',
      'polso_giugulare': 'polso_giugulare',
      'attivita': 'attivita',
      'tosse': 'tosse'
    };
    
    // Aggiungi tutti i campi mappati
    Object.entries(fieldMapping).forEach(([frontendKey, backendKey]) => {
      const value = formData[frontendKey as keyof RefertoCompleto];
      if (value && value.toString().trim()) {
        formDataToSend.append(backendKey, value.toString());
        console.log(`📤 [FORM] ${backendKey}:`, value.toString().substring(0, 50) + (value.toString().length > 50 ? '...' : ''));
      }
    });
    
    // Log finale dei campi inviati
    console.log('📤 [FORM] FormData preparato con', Array.from(formDataToSend.keys()).length, 'campi');
    console.log('📤 [FORM] Campi:', Array.from(formDataToSend.keys()));
    
    return formDataToSend;
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center p-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  // Componente per il rendering del contenuto dello step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1(); // Cliente
      case 2:
        return renderStep3(); // Dati Generali
      case 3:
        return renderStep5(); // Ecocardiografia
      case 4:
        return renderStep2(); // Anamnesi
      case 5:
        return renderStep4(); // Esame Clinico
      case 6:
        return renderStep6(); // Conclusioni
      default:
        return <div>Step non valido</div>;
    }
  };

  // Step 1: Upload PDF + Cliente
  const renderStep1 = () => (
    <Card>
      <Card.Header>
        <h5>
          <Upload className="me-2" />
          {getStepLabel(1)}
        </h5>
        <small className="text-muted">{getStepDescription(1)}</small>
      </Card.Header>
      <Card.Body>
        {/* Upload PDF */}
      {/*   <div className="mb-4">
          <h6>Carica PDF Misurazioni</h6>
          <Form.Group>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                  handlePDFUpload(file);
                }
              }}
              disabled={pdfLoading}
            />
            {pdfLoading && (
              <div className="text-center mt-2">
                <Spinner size="sm" className="me-2" />
                Elaborazione del PDF in corso...
              </div>
            )}
          </Form.Group>
        </div> */}

        {/* Tabella misurazioni PDF */}
       {/*  {pdfData && (
          <div className="mb-4">
            <Alert variant="success">
              <Check className="me-2" />
              PDF elaborato con successo!
              <div className="mt-2">
                <strong>Paziente:</strong> {pdfData.patient_name} | 
                <strong className="ms-2">Data esame:</strong> {pdfData.exam_date}
              </div>
            </Alert>
            <Table responsive striped size="sm">
              <thead>
                <tr>
                  <th>Nome Misurazione</th>
                  <th>Valore</th>
                  <th>Nome Misurazione</th>
                  <th>Valore</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pdfData.measurements).map(([sectionTitle, measurements]) => (
                  <React.Fragment key={sectionTitle}>
                    <tr>
                      <td colSpan={4} className="table-dark fw-bold text-center">
                        {sectionTitle}
                      </td>
                    </tr>
                    {Array.from({ length: Math.ceil((measurements as any[])?.length / 2 || 0) }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        {Array.from({ length: 2 }).map((_, colIndex) => {
                          const measurement = (measurements as any[])?.[rowIndex * 2 + colIndex];
                          return (
                            <React.Fragment key={colIndex}>
                              <td>{measurement?.name || ''}</td>
                              <td><strong>{measurement?.value || ''}</strong></td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )} */}

        {/* Ricerca Cliente */}
        <div className="mb-4">
          <h6>Seleziona Cliente</h6>
          <Form.Group className="position-relative">
            <Form.Control
              type="text"
              placeholder="Cerca tra i tuoi clienti..."
              value={searchQuery}
              onChange={(e) => handleClientSearch(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
            />
            {showSearchResults && searchResults.length > 0 && (
              <div 
                className="position-absolute w-100 bg-white border rounded shadow" 
                style={{ top: '100%', zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
              >
                <ListGroup variant="flush">
                  {searchResults.map((cliente) => (
                    <ListGroup.Item
                      key={cliente.id}
                      action
                      onClick={() => selectCliente(cliente)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                    >
                      <span><strong>{cliente.nome} {cliente.cognome}</strong></span>
                      <Badge bg="secondary">{cliente.email || 'N/D'}</Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
            
            
          </Form.Group>
        </div>

        {/* Dati Cliente */}
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={formData.cliente_nome || ''}
                onChange={(e) => handleInputChange('cliente_nome', e.target.value)}
                placeholder="Nome cliente"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Cognome</Form.Label>
              <Form.Control
                type="text"
                value={formData.cliente_cognome || ''}
                onChange={(e) => handleInputChange('cliente_cognome', e.target.value)}
                placeholder="Cognome cliente"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Indirizzo</Form.Label>
              <Form.Control
                type="text"
                value={formData.cliente_indirizzo || ''}
                onChange={(e) => handleInputChange('cliente_indirizzo', e.target.value)}
                placeholder="Indirizzo"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Città</Form.Label>
              <Form.Control
                type="text"
                value={formData.cliente_citta || ''}
                onChange={(e) => handleInputChange('cliente_citta', e.target.value)}
                placeholder="Città"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Codice Fiscale</Form.Label>
              <Form.Control
                type="text"
                value={formData.cliente_codice_fiscale || ''}
                onChange={(e) => handleInputChange('cliente_codice_fiscale', e.target.value)}
                placeholder="Codice Fiscale"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.cliente_email || ''}
                onChange={(e) => handleInputChange('cliente_email', e.target.value)}
                placeholder="Email"
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Step 2: Anamnesi
  const renderStep2 = () => (
    <Card>
      <Card.Header>
        <h5>
          <FileText className="me-2" />
          {getStepLabel(2)}
        </h5>
        <small className="text-muted">{getStepDescription(2)}</small>
      </Card.Header>
      <Card.Body>
        {/* Contenuto da definire */}
      </Card.Body>
    </Card>
  );

  // Step 3: Dati Generali
  const renderStep3 = () => (
    <Card>
      <Card.Header>
        <h5>{getStepLabel(3)}</h5>
        <small className="text-muted">{getStepDescription(3)}</small>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nome del Paziente</Form.Label>
              <Form.Control
                type="text"
                value={formData.nome_paziente || ''}
                onChange={(e) => handleInputChange('nome_paziente', e.target.value)}
                placeholder="Nome del paziente"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Specie del Paziente</Form.Label>
              <Form.Select
                value={formData.specie_paziente || ''}
                onChange={(e) => handleInputChange('specie_paziente', e.target.value)}
              >
                <option value="">Seleziona specie</option>
                <OpzioniOptions campo="specie_paziente" opzioni={opzioni} current={formData.specie_paziente} />
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Razza del Paziente</Form.Label>
              <Form.Control
                type="text"
                value={formData.razza_paziente || ''}
                onChange={(e) => handleInputChange('razza_paziente', e.target.value)}
                placeholder="Razza del paziente"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sesso del Paziente</Form.Label>
              <Form.Select
                value={formData.sesso_paziente || ''}
                onChange={(e) => handleInputChange('sesso_paziente', e.target.value)}
              >
                <option value="">Seleziona sesso</option>
                <OpzioniOptions campo="sesso_paziente" opzioni={opzioni} current={formData.sesso_paziente} />
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Peso del Paziente</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                value={formData.peso_paziente || ''}
                onChange={(e) => handleInputChange('peso_paziente', e.target.value)}
                placeholder="Peso in kg"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Età del Paziente</Form.Label>
              <Form.Control
                type="text"
                value={formData.age_paziente || ''}
                onChange={(e) => handleInputChange('age_paziente', e.target.value)}
                placeholder="Età"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Medico Referente</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  value={searchQueryMedico}
                  onChange={(e) => handleMedicoSearch(e.target.value)}
                  placeholder="Cerca medico referente..."
                  style={{
                    backgroundColor: selectedMedico ? '#e8f5e8' : 'white',
                    border: selectedMedico ? '2px solid #28a745' : '1px solid #ced4da'
                  }}
                />
                
                {/* Dropdown risultati ricerca medici */}
                {showSearchResultsMedico && searchResultsMedico.length > 0 && (
                  <ListGroup style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #ced4da',
                    borderTop: 'none'
                  }}>
                    {searchResultsMedico.map((medico, index) => (
                      <ListGroup.Item
                        key={index}
                        action
                        onClick={() => selectMedico(medico)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <strong>{medico.nome} {medico.cognome || ''}</strong>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                
                {/* Bottone e form per creare nuovo medico */}
                {showCreateMedico && searchQueryMedico.length > 2 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderTop: 'none',
                    padding: '10px'
                  }}>
                    <div className="mb-2">
                      <small className="text-muted">Nessun medico trovato</small>
                    </div>
                    <Row>
                      <Col md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Nome"
                          value={newMedicoData.nome}
                          onChange={(e) => setNewMedicoData(prev => ({ ...prev, nome: e.target.value }))}
                          size="sm"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Control
                          type="text"
                          placeholder="Cognome"
                          value={newMedicoData.cognome}
                          onChange={(e) => setNewMedicoData(prev => ({ ...prev, cognome: e.target.value }))}
                          size="sm"
                        />
                      </Col>
                    </Row>
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-2"
                      onClick={handleCreateMedico}
                      disabled={!newMedicoData.nome.trim() || !newMedicoData.cognome.trim()}
                    >
                      Crea medico referente
                    </Button>
                  </div>
                )}
                
                {/* Badge di conferma se medico selezionato */}
                {selectedMedico && (
                  <Badge 
                    bg="success" 
                    style={{ 
                      position: 'absolute', 
                      top: '5px', 
                      right: '5px' 
                    }}
                  >
                    <Check size={12} />
                  </Badge>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Motivo Visita</Form.Label>
              <Form.Control
                type="text"
                value={formData.motivo_visita || ''}
                onChange={(e) => handleInputChange('motivo_visita', e.target.value)}
                placeholder="Motivo della visita"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Terapia in Atto</Form.Label>
              <Form.Control
                type="text"
                value={formData.terapia_attuale || ''}
                onChange={(e) => handleInputChange('terapia_attuale', e.target.value)}
                placeholder="Terapia attuale"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Anamnesi</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.anamnesi || ''}
                onChange={(e) => handleInputChange('anamnesi', e.target.value)}
                placeholder="Anamnesi"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Data Referto *</Form.Label>
              <Form.Control
                type="date"
                value={formData.data_referto || ''}
                onChange={(e) => handleInputChange('data_referto', e.target.value)}
                required
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Step 4: Esame Clinico
  const renderStep4 = () => (
    <Card>
      <Card.Header>
        <h5>{getStepLabel(4)}</h5>
        <small className="text-muted">{getStepDescription(4)}</small>
      </Card.Header>
      <Card.Body>
        {/* Categorie dinamiche per esame clinico */}
        {categorie
          .filter(cat => ['Respiro', 'Soffio cardiaco'].includes(cat.categoria))
          .map(categoria => (
            <Card key={categoria.categoria_id} className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">{categoria.categoria}</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  {categoria.campi.map((campo: any) => (
                    <Col md={6} key={campo.campo_id} className="mb-3">
                      <Form.Group>
                        <Form.Label>{campo.campo}</Form.Label>
                        <Form.Select
                          value={selectedValues[categoria.categoria]?.[campo.campo] || ''}
                          onChange={(e) => updateSelectedValue(
                            categoria.categoria, 
                            campo.campo, 
                            e.target.value ? parseInt(e.target.value) : null
                          )}
                        >
                          <option value="">Seleziona un valore</option>
                          {campo.valori.map((valore: any) => (
                            <option key={valore.id} value={valore.id}>
                              {valore.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}

        {/* Campi statici per esame clinico */}
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Header className="bg-light">
            <h6 className="mb-0">Parametri Clinici</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Frequenza Cardiaca (bpm)</Form.Label>
                  <Form.Select
                    value={formData.frequenza_cardiaca || ''}
                    onChange={(e) => handleInputChange('frequenza_cardiaca', e.target.value)}
                  >
                    <option value="">Seleziona valore</option>
                    <OpzioniOptions campo="frequenza_cardiaca" opzioni={opzioni} current={formData.frequenza_cardiaca} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ritmo Cardiaco</Form.Label>
                  <Form.Select
                    value={formData.ritmo_cardiaco || ''}
                    onChange={(e) => handleInputChange('ritmo_cardiaco', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="ritmo_cardiaco" opzioni={opzioni} current={formData.ritmo_cardiaco} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mucose Esplorabili</Form.Label>
                  <Form.Select
                    value={formData.mucose || ''}
                    onChange={(e) => handleInputChange('mucose', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="mucose" opzioni={opzioni} current={formData.mucose} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>TRC</Form.Label>
                  <Form.Select
                    value={formData.trc || ''}
                    onChange={(e) => handleInputChange('trc', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="trc" opzioni={opzioni} current={formData.trc} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Itto Cardiaco</Form.Label>
                  <Form.Select
                    value={formData.itto_cardiaco || ''}
                    onChange={(e) => handleInputChange('itto_cardiaco', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="itto_cardiaco" opzioni={opzioni} current={formData.itto_cardiaco} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Polso Femorale</Form.Label>
                  <Form.Select
                    value={formData.polso_femorale || ''}
                    onChange={(e) => handleInputChange('polso_femorale', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="polso_femorale" opzioni={opzioni} current={formData.polso_femorale} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Polso Giugulare</Form.Label>
                  <Form.Select
                    value={formData.polso_giugulare || ''}
                    onChange={(e) => handleInputChange('polso_giugulare', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="polso_giugulare" opzioni={opzioni} current={formData.polso_giugulare} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Attività</Form.Label>
                  <Form.Select
                    value={formData.attivita || ''}
                    onChange={(e) => handleInputChange('attivita', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="attivita" opzioni={opzioni} current={formData.attivita} />
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tosse</Form.Label>
                  <Form.Select
                    value={formData.tosse || ''}
                    onChange={(e) => handleInputChange('tosse', e.target.value)}
                  >
                    <option value="">Seleziona stato</option>
                    <OpzioniOptions campo="tosse" opzioni={opzioni} current={formData.tosse} />
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Note Aggiuntive</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.note_esame || ''}
                    onChange={(e) => handleInputChange('note_esame', e.target.value)}
                    placeholder="Inserisci eventuali note sull'esame clinico"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );

  // Step 5: Ecocardiografia
  const renderStep5 = () => (
    <Card>
      <Card.Header>
        <h5>{getStepLabel(5)}</h5>
        <small className="text-muted">{getStepDescription(5)}</small>
      </Card.Header>
      <Card.Body>
        {/* Categorie dinamiche per ecocardiografia */}
        {categorie
          .filter(cat => !['Respiro', 'Soffio cardiaco'].includes(cat.categoria))
          .map(categoria => (
            <Card key={categoria.categoria_id} className="mb-3 border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">{categoria.categoria}</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  {categoria.campi.map((campo: any) => (
                    <Col md={6} key={campo.campo_id} className="mb-3">
                      <Form.Group>
                        <Form.Label>{campo.campo}</Form.Label>
                        <Form.Select
                          value={selectedValues[categoria.categoria]?.[campo.campo] || ''}
                          onChange={(e) => updateSelectedValue(
                            categoria.categoria, 
                            campo.campo, 
                            e.target.value ? parseInt(e.target.value) : null
                          )}
                        >
                          <option value="">Seleziona un valore</option>
                          {campo.valori.map((valore: any) => (
                            <option key={valore.id} value={valore.id}>
                              {valore.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          ))}

        {/* Note aggiuntive ecocardiografia */}
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Note Aggiuntive Ecocardiografia</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.note_eco || ''}
                onChange={(e) => handleInputChange('note_eco', e.target.value)}
                placeholder="Inserisci eventuali note aggiuntive"
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Step 6: Conclusioni
  const renderStep6 = () => (
    <Card>
      <Card.Header>
        <h5>{getStepLabel(6)}</h5>
        <small className="text-muted">{getStepDescription(6)}</small>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Conclusioni</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.conclusioni_referto || ''}
                onChange={(e) => handleInputChange('conclusioni_referto', e.target.value)}
                placeholder="Conclusioni del referto"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Indicazioni Terapeutiche</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={formData.indicazioni_terapeutiche || ''}
                onChange={(e) => handleInputChange('indicazioni_terapeutiche', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Prossimo Controllo</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.prossimo_controllo || ''}
                onChange={(e) => handleInputChange('prossimo_controllo', e.target.value)}
                placeholder="Indicazioni per il prossimo controllo"
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Azione finale */}
        <Row className="mt-4">
          <Col className="text-center">
            <Button
              variant="primary"
              onClick={handleGenerateReferto}
              disabled={submitting || !pdfData}
              size="lg"
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <FileText className="me-2" />
                  Genera Referto
                </>
              )}
            </Button>
            <small className="text-muted d-block mt-2">
              Genera il referto finale dopo aver completato tutti i campi
            </small>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  // Funzione per cancellare manualmente la bozza
  const handleClearDraft = () => {
    if (window.confirm('Sei sicuro di voler cancellare tutti i dati inseriti e ricominciare da capo?')) {
      clearDraftFromStorage();
      
      // Reset di tutti gli stati
      setFormData({
        data_referto: new Date().toISOString().split('T')[0],
        indicazioni_terapeutiche: `Si consiglia integrazione acidi grassi polinsaturi.
Si consigliano esami ematobiochimici ed urine con PU/CU.
Si raccomanda la valutazione della frequenza respiratoria a riposo in un minuto (app cardiodog CEVA), osservando che resti al di sotto dei 30/35 atm/min.
Si raccomanda esercizio fisico controllato, evitare situazioni di stress e caldo.
Si raccomanda visita clinica presso il medico curante per tosse, affaticamento, riluttanza all'esercizio, anoressia, aumento del volume addominale, aumento della frequenza respiratoria, debolezza, sincope, difficoltà respiratoria.`
      });
      setSelectedValues({});
      setSelectedCliente(null);
      setSearchQuery('');
      setSelectedMedico(null);
      setSearchQueryMedico('');
      setShowSearchResultsMedico(false);
      setShowCreateMedico(false);
      setNewMedicoData({ nome: '', cognome: '' });
      setPdfData(null);
      setPdfFile(null);
      setCurrentStep(1);
      setIsRestoredDraft(false);
      setIsEditingBozza(false);
      setEditingBozzaId(null);
      
      // Re-inizializza i valori delle categorie
      initializeSelectedValues(categorie);
      
      toast.success('Bozza cancellata, ricomincia da capo');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="outline-primary"
              className="btn-back"
              title="Torna ai Referti"
              aria-label="Torna ai Referti"
              onClick={() => {
                clearDraftFromStorage();
                navigate('/referti');
              }}
            >
              <ArrowLeft />
            </Button>
            
            {/* Pulsante per cancellare bozza (solo se ci sono dati da una bozza ripristinata) */}
            {isRestoredDraft && (
              <Button 
                variant="outline-danger"
                size="sm"
                onClick={handleClearDraft}
                title="Cancella tutti i dati e ricomincia da capo"
              >
                <Trash2 className="me-1" size={16} />
                Cancella Bozza
              </Button>
            )}
          </div>
          <h2>{isEditingBozza ? 'Modifica Bozza' : 'Nuovo Referto'}</h2>
        </Col>
      </Row>

      {/* Barra di progresso e navigazione step */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="fw-bold">Progresso: {currentStep}/{totalSteps}</span>
                  <span className="text-muted">{Math.round((currentStep / totalSteps) * 100)}%</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Step indicators */}
              <div className="d-flex justify-content-between">
                {Array.from({ length: totalSteps }, (_, index) => {
                  const stepNumber = index + 1;
                  const isCompleted = stepNumber < currentStep;
                  const isCurrent = stepNumber === currentStep;
                  const isAccessible = stepNumber <= currentStep;

                  return (
                    <div 
                      key={stepNumber}
                      className={`text-center ${isAccessible ? 'cursor-pointer' : ''}`}
                      onClick={() => isAccessible && goToStep(stepNumber)}
                      style={{ flex: 1, cursor: isAccessible ? 'pointer' : 'default' }}
                    >
                      <div className="mb-2">
                        <div 
                          className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
                            isCompleted 
                              ? 'bg-success text-white' 
                              : isCurrent 
                                ? 'bg-primary text-white' 
                                : 'bg-light text-muted'
                          }`}
                          style={{ width: '40px', height: '40px' }}
                        >
                          {isCompleted ? <Check size={20} /> : stepNumber}
                        </div>
                      </div>
                      <small className={`d-block ${isCurrent ? 'fw-bold text-primary' : isCompleted ? 'text-success' : 'text-muted'}`}>
                        {getStepLabel(stepNumber)}
                      </small>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contenuto dello step corrente */}
      <Form onSubmit={(e) => { e.preventDefault(); handleGenerateReferto(e); }}>
        <Row className="mb-4">
          <Col>
            {renderStepContent()}
          </Col>
        </Row>

        {/* Navigazione tra step */}
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Button
                    variant="outline-secondary"
                    className="btn-back"
                    title="Indietro"
                    aria-label="Indietro"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                  >
                    <ArrowLeft />
                  </Button>
                  
                  <div className="text-center">
                    <small className="text-muted">
                      Step {currentStep} di {totalSteps}
                    </small>
                  </div>

                  {currentStep < totalSteps ? (
                    <Button 
                      variant="primary" 
                      onClick={nextStep}
                    >
                      Avanti
                      <ChevronRight className="ms-1" />
                    </Button>
                  ) : (
                    <div></div>
                  )}
                </div>
                
                {/* Bottone Salva Bozza presente in tutti gli step */}
                <div className="text-center">
                  <Button
                    variant="light"
                    className="btn-soft"
                    onClick={handleSaveBozza}
                    disabled={submitting}
                    size="sm"
                  >
                    <Save size={16} />
                    {isEditingBozza ? 'Aggiorna Bozza' : 'Salva Bozza'}
                  </Button>
                  <small className="text-muted d-block mt-1">
                    {isEditingBozza ? 'Aggiorna le modifiche alla bozza' : 'Puoi salvare il tuo lavoro in qualsiasi momento'}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default NuovoReferto;